import type { ReactNode, ComponentType } from "react";
import type { CopyOption } from "../../Display/CopyableValue";

export interface StatItem {
  /** Display label for the stat. */
  label: string;
  /** Unique key for React rendering. Defaults to label. */
  statKey?: string | undefined;
  /** Icon rendered after the label text. */
  labelIcon?: ComponentType<{ className?: string }> | undefined;
  /** Color for the labelIcon. */
  labelIconColor?: string | undefined;
  /** Arbitrary content rendered after the label (e.g. indicator icons). */
  afterLabel?: ReactNode | undefined;
  value: ReactNode;
  /** Plain text value for clipboard copy (defaults to stringified value) */
  copyValue?: string | undefined;
  copyOptions?: CopyOption[] | undefined;
  /**
   * Action element (e.g. a Button) rendered after the value, OUTSIDE the
   * copy-on-click affordance — so a stat can be both copyable and actionable
   * without the two clicks colliding.
   */
  action?: ReactNode | undefined;
  /** Optional tooltip content shown via an info icon next to the label */
  tooltip?: ReactNode;
  /** When set, formats as "X.X%" and colors green (≥ threshold) or orange (< threshold).
   *  Overrides `value` — pass the raw number here instead. A `secondary` renders
   *  in parentheses after the primary (e.g. settled margin beside overall
   *  margin), colored independently by its own threshold. */
  percentage?:
    | {
        value: number;
        threshold: number;
        secondary?: { value: number; threshold: number } | undefined;
      }
    | undefined;
  /** When set, makes the stat value a clickable link. */
  href?: string | undefined;
  /** Opens `href` in a new tab with safe rel attributes. For links that leave
   *  the app entirely (Front, Google Maps) so the operator keeps their place. */
  hrefExternal?: boolean | undefined;
  /**
   * Makes the stat value a button that runs this on click. For stats that drive
   * the view they sit above — pinning matching rows to the top of a table —
   * where `href` would navigate away and `action` would add a second control.
   * Ignored when `href` is set.
   */
  onClick?: (() => void) | undefined;
  /** Draws an `onClick` stat as pressed, so the stat driving the current view is
   *  visible and clicking it again reads as switching it back off. */
  active?: boolean | undefined;
  /** Max width in pixels for the value. Truncates with ellipsis when exceeded. */
  maxWidth?: number | undefined;
}

/** Vertical divider between groups of stats. */
export interface StatDivider {
  type: "divider";
  dividerId: string;
}

/** A stat entry is either a stat item or a divider. */
export type StatEntry = StatItem | StatDivider;

/** Convenience constant for inserting a divider in a stats array. */
let dividerCounter = 0;
export function statDivider(): StatDivider {
  return { type: "divider", dividerId: `stat-divider-${++dividerCounter}` };
}

/** Type guard: returns true if the entry is a divider. */
export function isStatDivider(entry: StatEntry): entry is StatDivider {
  return "type" in entry && entry.type === "divider";
}
